import { cn } from '@/lib/utils';
import { fmtCurrency } from '@/lib/cobalt-helpers';

export interface PipelineSegment {
  label: string;
  amount: number;
  /** A CSS color string (full color or `hsl(var(--...))`). */
  color: string;
}

interface Props {
  segments: PipelineSegment[];
  className?: string;
}

/**
 * Single-bar stage pipeline visualization for the Opportunities top strip.
 * Each segment's width is proportional to its $ amount; the inline label
 * shows the segment's value when there's room.
 */
export function StagePipelineBar({ segments, className }: Props) {
  const total = segments.reduce((s, x) => s + x.amount, 0) || 1;

  return (
    <div className={className}>
      <div className="flex h-7 overflow-hidden rounded-md bg-muted">
        {segments.map((s) => (
          <div
            key={s.label}
            className="flex items-center border-r-2 border-card pl-2.5 last:border-r-0"
            style={{
              flexGrow: s.amount,
              flexShrink: 0,
              flexBasis: 0,
              background: `color-mix(in oklab, ${s.color} 22%, transparent)`,
            }}
            title={`${s.label} — ${fmtCurrency(s.amount)}`}
          >
            <span
              className="truncate text-[11.5px] font-semibold tabular-nums"
              style={{ color: s.color }}
            >
              {fmtCurrency(s.amount, { compact: true })}
            </span>
          </div>
        ))}
      </div>

      <div className="mt-2.5 flex flex-wrap gap-x-4 gap-y-1.5">
        {segments.map((s) => (
          <div key={s.label} className="flex items-center gap-1.5 text-xs text-foreground/80">
            <span className="h-2 w-2 rounded-sm" style={{ background: s.color }} />
            <span>{s.label}</span>
            <span className="tabular-nums text-muted-foreground">
              · {Math.round((s.amount / total) * 100)}%
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}
